import { Role } from './role.model';
import { Privilege } from './privilege.model';
import { UserShowRole } from './user-show-role.model';

export class User {

    public id: number;
    public firstName = '';
    public lastName = '';
    public username = '';
    public voornaam = '';
    public achternaam = '';
    public email = '';
    public emailConfirmed: boolean = false;
    /**
     * True when an admin has issued the user a temp password via the user-edit
     * Beheer section. The nav-redirect in app.component sends users with this
     * flag to /force-change-password until they pick a real one.
     */
    public mustChangePassword: boolean = false;
    public rolId = 0;
    public status = '';
    public activeShow: { id: number; name: string; useLegacyShowreport?: boolean; hasLog?: boolean } | null = null;
    public roles: Role[] = [];
    public privilages: Privilege[] = [];
    public shows: any[] = [];

    // Properties for being planable
    // TODO Roy Schrauwen 20-3-2025: Move to a new class
    past90DayServices?: Map<number, boolean>;
    past90DayServiceCount?: number;
    currentMonthAssignedCount?: number;
    preferredDays?: string[];
    lessPriorityInPlanning?: boolean = false;
    strategicPlanningPenalty?: number = 3;

    public calendarToken?: string;
    public profilePictureUrl?: string | null;

    public isPlannable: boolean = true;
    public showCompletedMaintenanceTasksConfetti: boolean = true;

    /**
     * Show-specific role assignments for this user.
     * Defines which roles (e.g., stagemanager, lighting) the user can fill per show.
     * Loaded separately via the user show roles API.
     */
    public showRoles: UserShowRole[] = [];

    // public static create(
    //     id: number,
    //     username: string,
    //     voornaam: string,
    //     achternaam: string,
    //     email: string,
    //     rolId: number
    // ): User {
    //     const instance = new User();
    //     instance.id = id;
    //     instance.username = username;
    //     instance.voornaam = voornaam;
    //     instance.achternaam = achternaam;
    //     instance.email = email;
    //     instance.rolId = rolId;
    //     return instance;
    // }
    //
    // public static createFromJsonDepricated(json: any): User {
    //     if (json == null) {
    //         throw new Error('User.createFromJson: json is null');
    //     }
    //     const user = new User();
    //     user.activeShow = json.activeShow;
    //     user.email = json.email;
    //     user.emailConfirmed = json.emailConfirmed;
    //     user.firstName = json.firstName;
    //     user.id = json.id;
    //     user.lastName = json.lastName;
    //     user.roles = json.roles.map((role: any) => Role.createFromJson(role));
    //     user.shows = json.shows; // TODO Roy Schrauwen 20-3-2025: Create object for shows
    //     user.status = json.status;
    //     user.username = json.username;
    //     user.rolId = json.rolId;
    //
    //     let allPrivilegesForAllRoles: Privilege[] = [];
    //     user.roles.forEach((role: Role) => {
    //         allPrivilegesForAllRoles = allPrivilegesForAllRoles.concat(role.privileges);
    //     });
    //     user.privilages = allPrivilegesForAllRoles;
    //
    //     return user;
    // }

    public static fromJson(json: any): User {
        const instance = new User();
        instance.id = json.id;
        instance.username = json.username;
        instance.voornaam = json.voornaam;
        instance.achternaam = json.achternaam;
        instance.email = json.email;
        instance.rolId = json.rolId;
        return instance;
    }

    public createFromJson(json: any): User {
        if (json == null) {
            throw new Error('User.createFromJson: json is null');
        }
        const user = new User();
        user.activeShow = json.activeShow;
        user.email = json.email;
        user.emailConfirmed = json.emailConfirmed;
        user.mustChangePassword = !!json.mustChangePassword;
        user.firstName = json.firstName;
        user.id = json.id;
        user.lastName = json.lastName;
        user.roles = Array.isArray(json.roles)            ? json.roles.map((role: any) => Role.createFromJson(role))            : []; // Als roles null of undefined is, maak een lege array
        // Use the shows array from the backend - trust the server's response
        if (Array.isArray(json.shows)) {
            user.shows = json.shows;
        } else {
            user.shows = [];
        }
        user.status = json.status;
        user.username = json.username;
        user.rolId = json.rolId;

        // Handle privileges - can come in two formats:
        // 1. /users/me: flat array of strings directly on user: ["template_view", "users_admin", ...]
        // 2. /users/{id}: array of objects nested in roles: roles[].privileges[].name
        if (Array.isArray(json.privileges) && json.privileges.length > 0) {
            // Format 1: privileges as flat array (from /users/me)
            if (typeof json.privileges[0] === 'string') {
                // Array of privilege name strings
                user.privilages = json.privileges.map((name: string) => ({ id: 0, name, prettyName: null }));
            } else {
                // Array of privilege objects
                user.privilages = json.privileges.map((p: any) => Privilege.createFromJson(p));
            }
        } else {
            // Format 2: extract from roles (from /users/{id})
            let allPrivilegesForAllRoles: Privilege[] = [];
            user.roles.forEach((role: Role) => {
                allPrivilegesForAllRoles = allPrivilegesForAllRoles.concat(role.privileges);
            });
            user.privilages = allPrivilegesForAllRoles;
        }

        user.isPlannable = json.isPlannable ?? true;
        user.showCompletedMaintenanceTasksConfetti = json.showCompletedMaintenanceTasksConfetti ?? true;
        user.profilePictureUrl = json.profilePictureUrl ?? null;

        return user;
    }

    public toJson(): any {
        return {
            id: this.id,
            username: this.username,
            voornaam: this.voornaam,
            achternaam: this.achternaam,
            email: this.email,
            rolId: this.rolId
        };
    }

    public getId(): number {
        return this.id;
    }

    public setUsername(username: string) {
        this.username = username;
    }

    public getVoornaam(): string {
        return this.voornaam;
    }

    public getAchternaam(): string {
        return this.achternaam;
    }

    public setVoornaam(voornaam: string): void {
        this.voornaam = voornaam;
    }

    public setAchternaam(achternaam: string): void {
        this.achternaam = achternaam;
    }

    public getVolledigeNaam(): string {
        return this.voornaam + ' ' + this.achternaam;
    }

    public getFullName(): string {
        return this.firstName + ' ' + this.lastName;
    }

    public getUsername(): string {
        return this.username;
    }

    public getEmail(): string {
        return this.email;
    }

    public setEmail(email: string): void {
        this.email = email;
    }

    public getRolId(): number {
        return this.rolId;
    }

    public setRolId(rolId: number): void {
        this.rolId = rolId;
    }

    public setActiveShow(showId: number, showName: string, useLegacyShowreport: boolean = false): void {
        this.activeShow = {id: showId, name: showName, useLegacyShowreport};
    }

    public hasPrivilege(privilegeName: string): boolean {
        return this.privilages.some(privilege => privilege.name === privilegeName);
    }

    public hasRole(roleName: string): boolean {
        return this.roles.some(role => role.name === roleName);
    }

    /**
     * True for system administrators. Checks the ROLE_Admin role, falling back
     * to the `privileges_admin` privilege — /users/me returns privileges as a
     * flat list but may not include the roles array, so the role check alone
     * can be unreliable for the current user.
     */
    public isAdmin(): boolean {
        return this.hasRole('ROLE_Admin') || this.hasPrivilege('privileges_admin');
    }

}
